import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { User, Eye } from "lucide-react";

export interface UserDetail {
  name: string;
  email: string;
  role: string;
}

interface Props {
  adminCount: number;
  viewerCount: number;
  adminUsers: UserDetail[];
  viewerUsers: UserDetail[];
  onChangeAdmin: (users: UserDetail[]) => void;
  onChangeViewer: (users: UserDetail[]) => void;
}

export function OrderUserDetails({
  adminCount,
  viewerCount,
  adminUsers,
  viewerUsers,
  onChangeAdmin,
  onChangeViewer,
}: Props) {
  const { t } = useTranslation();

  const updateUser = (
    list: UserDetail[],
    idx: number,
    field: keyof UserDetail,
    value: string,
    setter: (u: UserDetail[]) => void
  ) => {
    const updated = [...list];
    updated[idx] = { ...updated[idx], [field]: value };
    setter(updated);
  };

  const renderUserForm = (
    users: UserDetail[],
    type: "admin" | "viewer",
    setter: (u: UserDetail[]) => void
  ) => {
    const Icon = type === "admin" ? User : Eye;
    const label = type === "admin" ? t("order.adminUser") : t("order.viewerUser");

    return users.map((user, idx) => (
      <div
        key={`${type}-${idx}`}
        className="p-5 rounded-xl border border-border bg-card space-y-4"
      >
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Icon className="h-4 w-4 text-primary" />
          {label} {idx + 1}
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label>{t("order.userName")} *</Label>
            <Input
              value={user.name}
              onChange={(e) => updateUser(users, idx, "name", e.target.value, setter)}
              placeholder={t("order.userNamePlaceholder")}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("order.userEmail")} *</Label>
            <Input
              type="email"
              value={user.email}
              onChange={(e) => updateUser(users, idx, "email", e.target.value, setter)}
              placeholder={t("order.userEmailPlaceholder")}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("order.userRole")}</Label>
            <Input
              value={user.role}
              onChange={(e) => updateUser(users, idx, "role", e.target.value, setter)}
              placeholder={t("order.userRolePlaceholder")}
            />
          </div>
        </div>
      </div>
    ));
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold text-foreground">
          {t("order.userDetails")}
        </h2>
        <p className="text-muted-foreground mt-1">
          {t("order.userDetailsDesc")}
        </p>
      </div>

      {adminCount > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground">
            {t("order.adminUsers")} ({adminCount})
          </h3>
          {renderUserForm(adminUsers, "admin", onChangeAdmin)}
        </div>
      )}

      {viewerCount > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground">
            {t("order.viewerUsers")} ({viewerCount})
          </h3>
          {renderUserForm(viewerUsers, "viewer", onChangeViewer)}
        </div>
      )}

      {adminCount === 0 && viewerCount === 0 && (
        <p className="text-muted-foreground italic">{t("order.noUsersNeeded")}</p>
      )}
    </div>
  );
}
